import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, CornerDownRight } from 'lucide-react';
import { genericCommentApi, GenericComment } from '../api/comments';
import { ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useUserName } from '../hooks/useUserName';
import { UserPicker } from './UserPicker';
import { useToast } from './Toast';

/**
 * Reusable contextual discussion for any entity, on the ONE shared
 * comments table (migrations 011/023/049/097). `section` gives each tab
 * its own thread ("Comments for Employment" only shows what was posted
 * on that tab).
 *
 * Migration 097 added one-level threaded replies, optional "needs
 * resolution" items with who/when resolved, author withdrawal (soft -
 * the original text is retained server-side for audit but never sent
 * to the browser), and @mentions. Mentions are filtered server-side to
 * people who can genuinely view the record; anyone who can't is simply
 * not told, and the author is informed rather than the mention silently
 * vanishing.
 *
 * These are discussion, never workflow: nothing here approves, rejects,
 * or returns a record - those decisions and their mandatory reasons stay
 * in the workflow/approval actions on each page.
 *
 * Collapses to a compact bar when a thread is empty, so an unused
 * discussion never consumes space on a busy workspace.
 */
export function CommentsThread({ entityType, entityId, section, heading = 'Comments' }: { entityType: string; entityId: number; section?: string; heading?: string }) {
  const { showError, showSuccess } = useToast();
  const { user } = useAuth();
  const { userName } = useUserName();
  const queryClient = useQueryClient();
  const queryKey = ['comments', entityType, entityId, section ?? null];

  const [expandedOverride, setExpandedOverride] = useState<boolean | null>(null);
  const [body, setBody] = useState('');
  const [needsResolution, setNeedsResolution] = useState(false);
  const [mentionIds, setMentionIds] = useState<number[]>([]);
  const [replyingTo, setReplyingTo] = useState<number | null>(null);
  const [replyBody, setReplyBody] = useState('');

  const commentsQuery = useQuery({ queryKey, queryFn: () => genericCommentApi.list(entityType, entityId, section) });
  const comments = commentsQuery.data?.data ?? [];
  const visibleCount = comments.filter((c) => !c.withdrawn_at).length;
  const openCount = comments.filter((c) => c.resolution_status === 'open' && !c.withdrawn_at).length;
  const expanded = expandedOverride ?? comments.length > 0;

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey });
    queryClient.invalidateQueries({ queryKey: ['comment-counts', entityType, entityId] });
  };

  const createMutation = useMutation({
    mutationFn: (v: { text: string; parentCommentId?: number; requiresResolution?: boolean; mentionUserIds?: number[] }) =>
      genericCommentApi.create(entityType, entityId, v.text, section, { parentCommentId: v.parentCommentId, requiresResolution: v.requiresResolution, mentionUserIds: v.mentionUserIds }),
    onSuccess: (result, v) => {
      if (v.parentCommentId) { setReplyingTo(null); setReplyBody(''); } else { setBody(''); setNeedsResolution(false); setMentionIds([]); }
      const skipped = result.data.mentionsSkipped ?? [];
      if (skipped.length > 0) showError(`Posted, but ${skipped.map((id) => userName(id)).join(', ')} ${skipped.length === 1 ? 'was' : 'were'} not notified - they don't have access to this record.`);
      refresh();
    },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to post comment.'),
  });
  const resolveMutation = useMutation({
    mutationFn: (id: number) => genericCommentApi.resolve(id),
    onSuccess: () => { showSuccess('Marked as resolved.'); refresh(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to resolve.'),
  });
  const withdrawMutation = useMutation({
    mutationFn: (id: number) => genericCommentApi.withdraw(id),
    onSuccess: () => { showSuccess('Comment withdrawn.'); refresh(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to withdraw.'),
  });

  const topLevel = comments.filter((c) => c.parent_comment_id === null);
  const repliesFor = (id: number) => comments.filter((c) => c.parent_comment_id === id);

  function renderComment(c: GenericComment, isReply: boolean) {
    const withdrawn = !!c.withdrawn_at;
    return (
      <div key={c.id} className={'discussion-item' + (isReply ? ' discussion-reply' : '')}>
        <div className="discussion-meta">
          {isReply && <CornerDownRight size={13} className="muted" />}
          <strong>{c.author_name}</strong>
          <span className="muted">{new Date(c.created_at).toLocaleString()}</span>
          {!withdrawn && c.resolution_status === 'open' && <span className="discussion-pill discussion-pill-open">Needs resolution</span>}
          {!withdrawn && c.resolution_status === 'resolved' && (
            <span className="discussion-pill discussion-pill-resolved">Resolved{c.resolved_by_name ? ` by ${c.resolved_by_name}` : ''}{c.resolved_at ? ` · ${new Date(c.resolved_at).toLocaleDateString()}` : ''}</span>
          )}
        </div>
        <div className={withdrawn ? 'discussion-body discussion-withdrawn' : 'discussion-body'}>{withdrawn ? 'Comment withdrawn by author.' : c.body}</div>
        {!withdrawn && (
          <div className="discussion-actions">
            {!isReply && <button className="link-button" onClick={() => { setReplyingTo(replyingTo === c.id ? null : c.id); setReplyBody(''); }}>Reply</button>}
            {c.resolution_status === 'open' && <button className="link-button" onClick={() => resolveMutation.mutate(c.id)} disabled={resolveMutation.isPending}>Resolve</button>}
            {user?.id === c.created_by && (
              <button className="link-button" onClick={() => { if (window.confirm('Withdraw this comment? It will show as withdrawn; the original is kept in the audit record.')) withdrawMutation.mutate(c.id); }} disabled={withdrawMutation.isPending}>Withdraw</button>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <section className="card discussion-card">
      <button className="discussion-header" onClick={() => setExpandedOverride(!expanded)} aria-expanded={expanded}>
        {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <span className="discussion-heading">{heading}</span>
        {visibleCount > 0 && <span className="discussion-count">{visibleCount}</span>}
        {openCount > 0 && <span className="discussion-pill discussion-pill-open">{openCount} open</span>}
        {!expanded && comments.length === 0 && <span className="muted discussion-hint">Add a comment</span>}
      </button>

      {expanded && (
        <div className="discussion-content">
          {commentsQuery.isLoading && <p className="muted">Loading…</p>}
          {commentsQuery.data && topLevel.length === 0 && <p className="muted">No comments yet.</p>}
          {topLevel.map((c) => (
            <div key={c.id} className="discussion-thread">
              {renderComment(c, false)}
              {repliesFor(c.id).map((r) => renderComment(r, true))}
              {replyingTo === c.id && (
                <div className="discussion-reply-form">
                  <textarea rows={2} value={replyBody} onChange={(e) => setReplyBody(e.target.value)} placeholder={`Reply to ${c.author_name}…`} autoFocus />
                  <div className="discussion-composer-actions">
                    <button className="btn-secondary" onClick={() => setReplyingTo(null)}>Cancel</button>
                    <button onClick={() => { if (replyBody.trim()) createMutation.mutate({ text: replyBody, parentCommentId: c.id }); else showError('Reply cannot be empty.'); }} disabled={createMutation.isPending}>Reply</button>
                  </div>
                </div>
              )}
            </div>
          ))}

          <div className="discussion-composer">
            <textarea rows={2} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Write a comment…" />
            {mentionIds.length > 0 && (
              <div className="discussion-mentions">
                {mentionIds.map((id) => (
                  <span key={id} className="discussion-mention-chip">@{userName(id)} <button onClick={() => setMentionIds(mentionIds.filter((m) => m !== id))} aria-label="Remove mention">×</button></span>
                ))}
              </div>
            )}
            <div className="discussion-composer-actions">
              <UserPicker value="" onChange={(e) => { const id = Number(e.target.value); if (id && !mentionIds.includes(id) && mentionIds.length < 10) setMentionIds([...mentionIds, id]); }} aria-label="Mention someone" style={{ maxWidth: 200 }} />
              <label className="discussion-checkbox"><input type="checkbox" checked={needsResolution} onChange={(e) => setNeedsResolution(e.target.checked)} /> Needs resolution</label>
              <span style={{ flex: 1 }} />
              <button onClick={() => { if (body.trim()) createMutation.mutate({ text: body, requiresResolution: needsResolution, mentionUserIds: mentionIds }); else showError('Comment cannot be empty.'); }} disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Posting…' : 'Comment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
